// Числовой ввод без «прилипшего» нуля.
//
// Зачем: обычный <input type="number" value={0}> показывает зафиксированный 0,
// который приходится стирать вручную перед вводом. Здесь 0 показывается как
// placeholder (серым), поле остаётся пустым, а наружу всё равно отдаётся число.
//
// Особенности:
//   • во время ввода держим «сырой» текст — можно временно очистить поле,
//     набрать «12.» или «-» без того, чтобы значение скакало;
//   • на blur текст нормализуется из числового value;
//   • запятая принимается как десятичный разделитель;
//   • type="text" + inputMode — корректная клавиатура на мобильных и никаких
//     спиннеров, которые мешают.

import { forwardRef, useEffect, useRef, useState, type InputHTMLAttributes } from 'react';

type Props = Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange' | 'type'> & {
  value: number;
  onChange: (value: number) => void;
  /** Разрешить дробные значения (по умолчанию да). Для «порядка сортировки» — false. */
  decimal?: boolean;
  /** Разрешить отрицательные значения (по умолчанию нет). */
  allowNegative?: boolean;
  /** Показывать 0 как placeholder, а не как значение (по умолчанию да). */
  zeroAsPlaceholder?: boolean;
};

function sanitize(raw: string, decimal: boolean, allowNegative: boolean): string {
  let s = raw.replace(',', '.');
  s = s.replace(decimal ? /[^0-9.\-]/g : /[^0-9\-]/g, '');
  // минус — только в самом начале
  if (allowNegative) s = s[0] === '-' ? '-' + s.slice(1).replace(/-/g, '') : s.replace(/-/g, '');
  else s = s.replace(/-/g, '');
  // единственная точка
  if (decimal) {
    const dot = s.indexOf('.');
    if (dot !== -1) s = s.slice(0, dot + 1) + s.slice(dot + 1).replace(/\./g, '');
  }
  return s;
}

export const NumberInput = forwardRef<HTMLInputElement, Props>(function NumberInput(
  {
    value,
    onChange,
    decimal = true,
    allowNegative = false,
    zeroAsPlaceholder = true,
    placeholder,
    onFocus,
    onBlur,
    ...rest
  },
  ref,
) {
  const toText = (n: number) => (zeroAsPlaceholder && n === 0 ? '' : String(n));
  const [text, setText] = useState<string>(toText(value));
  const focused = useRef(false);

  // Внешнее изменение value подхватываем только когда поле не редактируется,
  // чтобы не затирать промежуточный ввод.
  useEffect(() => {
    if (!focused.current) setText(toText(value));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, zeroAsPlaceholder]);

  return (
    <input
      ref={ref}
      type="text"
      inputMode={decimal ? 'decimal' : 'numeric'}
      placeholder={placeholder ?? '0'}
      value={text}
      onFocus={(e) => {
        focused.current = true;
        onFocus?.(e);
      }}
      onBlur={(e) => {
        focused.current = false;
        setText(toText(value));
        onBlur?.(e);
      }}
      onChange={(e) => {
        const next = sanitize(e.target.value, decimal, allowNegative);
        setText(next);
        if (next === '' || next === '-' || next === '.' || next === '-.') {
          onChange(0);
          return;
        }
        const n = Number(next);
        if (Number.isFinite(n)) onChange(n);
      }}
      {...rest}
    />
  );
});
